import * as React from 'react';
import { Theme, useTheme, SxProps } from '@mui/material/styles';
import OutlinedInput from '@mui/material/OutlinedInput';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { Box, IconButton } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import './CustomDropdown.scss';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

interface CustomDrodownProps {
	options: string[];
	value: string | string[];
	onChange: (value: string | string[]) => void;
	placeholder?: string;
	sx?: SxProps<Theme>;
	isMulti?: boolean;
	width?: number | string;
	className?: string;
	showCross?: boolean;
	optionWidth?: number;
}

function getStyles(name: string, selected: readonly string[], theme: Theme) {
	return {
		fontWeight: selected.includes(name)
			? theme.typography.fontWeightMedium
			: theme.typography.fontWeightRegular,
	};
}

const CustomDrodown: React.FC<CustomDrodownProps> = ({
	options,
	value,
	onChange,
	placeholder = 'Select options',
	sx,
	isMulti = false,
	className = '',
	width,
	showCross,
	optionWidth,
}) => {
	const theme = useTheme();
	const isLightTheme = theme.palette.mode === 'light';

	const MenuProps = {
		PaperProps: {
			className: 'custom-dropdown-menu',
			sx: {
				maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
				width: optionWidth || 250,
				overflowY: 'auto',
				scrollbarGutter: 'stable',
			},
		},
	};

	const handleChange = (event: any) => {
		const {
			target: { value: selected },
		} = event;

		if (isMulti) {
			onChange(typeof selected === 'string' ? selected.split(',') : selected);
		} else {
			onChange(selected);
		}
	};

	const handleClear = () => {
		onChange(isMulti ? [] : '');
	};

	return (
		<FormControl sx={{ width: width || 250, ...sx }}>
			<Box
				className={`custom-dropdown-container ${className}`}
				sx={{ position: 'relative', width: width || 250 }}
			>
				<Select
					className={`custom-dropdown ${isLightTheme ? 'light' : 'dark'}`}
					sx={{
						width: '100%',
						'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
							borderColor: isLightTheme
								? 'rgba(0, 0, 0, 0.23)'
								: 'rgba(255, 255, 255, 0.23)',
							borderWidth: '1px',
						},
					}}
					displayEmpty
					multiple={isMulti}
					value={value}
					onChange={handleChange}
					input={<OutlinedInput />}
					renderValue={(selected) => {
						if (
							(isMulti && Array.isArray(selected) && selected.length === 0) ||
							(!isMulti && selected === '')
						) {
							return <div style={{ color: 'gray' }}>{placeholder}</div>;
						}
						return isMulti
							? (selected as string[]).join(', ')
							: (selected as string);
					}}
					MenuProps={MenuProps}
					inputProps={{
						'aria-label': isMulti ? 'multi-select' : 'single-select',
					}}
				>
					{options.map((option) => (
						<MenuItem
							key={option}
							value={option}
							style={getStyles(
								option,
								isMulti ? (value as string[]) : [value as string],
								theme
							)}
						>
							{option}
						</MenuItem>
					))}
				</Select>

				{showCross &&
					((isMulti && (value as string[]).length > 0) ||
						(!isMulti && value !== '')) && (
						<IconButton
							aria-label='clear'
							size='small'
							onClick={handleClear}
							sx={{
								position: 'absolute',
								right: 24,
								top: '50%',
								transform: 'translateY(-50%)',
								padding: 0.5,
								opacity: 0,
								transition: 'opacity 0.2s ease-in-out',
								pointerEvents: 'auto',
								color: 'white',
							}}
							className='dropdown-clear-icon'
						>
							<ClearIcon fontSize='small' />
						</IconButton>
					)}
			</Box>
		</FormControl>
	);
};

export default CustomDrodown;
